import React, { useState } from 'react';
import './BrandIcon.scss';

interface BrandIconProps {
  /**
   * Real brand mask-image class (`ico--gpt-bck` etc.). Pass `null`/`undefined` to render
   * the colored-initial fallback — the source-of-truth helpers (`getPlatformIcon` /
   * `getBotIcon` in `shared/constants/botsActivityIcons`) already return `null` for
   * unknown brands.
   */
  iconClass?: string | null;
  /** Brand color — used as the background tint for the letter fallback. */
  color?: string;
  /** Displayed label, first character of which is used for the letter fallback. */
  name: string;
  /** Icon size token (matches global `ico--xs` / `ico--sm` / `ico--md`). */
  size?: 'xs' | 'sm' | 'md';
  /**
   * Brand domain — renders its favicon (`favicon.is`, same source as the Sources pages).
   * A domain we have no favicon for falls back to the letter badge, so the row never
   * shows a broken-image glyph.
   */
  domain?: string | null;
}

const getInitial = (name: string): string => {
  const trimmed = name.trim();
  if (!trimmed) return '?';

  return trimmed[0] === '#' ?
    trimmed[1].toUpperCase() :
    trimmed[0].toUpperCase();
};

/**
 * Renders a brand mark for AI platforms/bots:
 *
 * - When `iconClass` is provided, renders that mask-image icon (full-color brand logo).
 * - Otherwise, when `domain` is provided, renders that domain's favicon.
 * - When neither is available (or the favicon fails to load), falls back to a colored badge
 *   with the brand's first letter on a tinted background (brand color).
 *
 * Used in BotsActivity tiles + breakdown rows and in BotAccess's bot table — anywhere
 * we need a consistent visual identity for a known *and* unknown brand without falling
 * back to a generic gray robot icon.
 */
const BrandIcon: React.FC<BrandIconProps> = ({ iconClass, color, name, size = 'sm', domain }) => {
  // Trzymamy DOMENĘ, która się nie wczytała, nie samą flagę — po podmianie domeny marki flaga
  // zostałaby zapalona i nowy favicon nigdy by nie spróbował się pokazać.
  const [failedDomain, setFailedDomain] = useState<string | null>(null);

  if (iconClass) {
    return (
      <span
        className={`${iconClass} ico--${size} brand-icon brand-icon--logo`}
        aria-hidden="true"
      />
    );
  }

  if (domain && domain !== failedDomain) {
    return (
      <img
        src={`https://favicon.is/${domain}`}
        alt=""
        aria-hidden="true"
        className={`brand-icon brand-icon--favicon brand-icon--${size}`}
        onError={() => setFailedDomain(domain)}
      />
    );
  }

  return (
    <span
      className={`brand-icon brand-icon--initial brand-icon--${size}`}
      style={
        {
          '--brand-initial-color': color ?? 'var(--grey-ux-400)',
        } as React.CSSProperties
      }
      aria-hidden="true"
    >
      {getInitial(name)}
    </span>
  );
};

export { BrandIcon };
export default BrandIcon;
